<template>
  <card-dialog
    v-model="modalVisible"
    max-width="400px"
    :title="locationFormData.id ? $t('card_title.edit_location') : $t('card_title.add_location') "
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="location">
          {{ $t('card_title.edit_location') }}
        </div>
        <div v-else>
          {{ $t('new_location.dialog.header') }}
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="locationFormData.name"
            :error-messages="errors"
            :label="$t('text_input.location_name.title')"
            dense
            outlined
            :placeholder="$t('text_input.location_name.placeholder')"
            class="mb-4"
          />
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="locationFormData.id"
            color="error"
            text
            block
            class="mt-1"
            @click="toggleConfirmationDialog(true)"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteLocation()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { LocationApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { cloneDeep } from 'lodash'

const emptyLocationFormData = {
  id: undefined,
  name: ''
}

export default {
  name: 'LocationFormDialog',
  components: { DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    location: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      locationFormData: emptyLocationFormData,
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_LOCATION_FORM, () => {
      this.modalVisible = true
      this.locationFormData = cloneDeep(emptyLocationFormData)

      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    })

    EventBus.$on(this.$events.EDIT_LOCATION, (location) => {
      this.modalVisible = true

      this.locationFormData = {
        id: location.id,
        name: location.name
      }
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.locationFormData.name
        }

        let result

        if (this.locationFormData.id) {
          data.id = this.locationFormData.id

          result = await LocationApiService.update(data)
        } else {
          result = await LocationApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.location_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.locationFormData.id) {
          EventBus.$emit(this.$events.LOCATION_UPDATED)
        } else {
          EventBus.$emit(this.$events.LOCATION_CREATED)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.location_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteLocation() {
      try {
        this.deleting = true

        await LocationApiService.delete(this.locationFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.location_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.LOCATION_DELETED, this.locationFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.location_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
